"use client";

import { documentsInfo } from "@/info/documents";
import formikOptions from "@/utils/formikOptions";
import { FileText } from "lucide-react";
import { memo, useEffect, useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { Input } from "./rhf/input";

const nonKhataDocumentOptions = documentsInfo.filter(d => !d.isKhataDoc && d.code === "sanctionedPlan");

// interface DocumentsDetails {
// 	approvalDocs: string[];
// 	constructionKhathaAuthority: string;
// 	constructionKhatha: string;
// 	landKhathaAuthority: string;
// 	landKhatha: string;
// }

// Extract unique authorities from documentsInfo and create options
const getAuthorityOptions = (type: "isConstructionDoc" | "isLandDoc") => {
	const authorities = new Set<string>();

	documentsInfo.forEach(doc => {
		if (doc.from && doc[type]) authorities.add(doc.from);
	});

	// Convert to options format with proper labels and values
	const options = Array.from(authorities).map(authority => {
		const value = authority.toLowerCase();
		return {
			label: authority === "Unknown" ? "Unsure" : authority,
			value: value === "unknown" ? "unknown" : value,
		};
	});

	// Sort alphabetically, but put "Unknown/Unsure, Other" at the end
	return options.sort((a, b) => {
		if (["unknown", "unsure", "other", "others"].includes(a.value)) return 1;
		if (["unknown", "unsure", "other", "others"].includes(b.value)) return -1;
		return a.label.localeCompare(b.label);
	});
};

// Get khatha options based on selected authority
const getKhathaOptions = (type: "constructionKhatha" | "landKhatha", authority: string) => {
	if (!authority) return [];

	// Filter documents from documentsInfo based on type and authority
	const filteredDocs = documentsInfo.filter(doc => {
		// Check if it's a khatha document
		if (!doc.isKhataDoc) return false;

		// Check type
		if (type === "constructionKhatha" && !doc.isConstructionDoc) return false;
		if (type === "landKhatha" && !doc.isLandDoc) return false;

		// Check authority
		if (authority === "unknown") return doc.from === "Unknown";
		return doc.from?.toLowerCase() === authority.toLowerCase();
	});

	return formikOptions(filteredDocs);
};

// function isDocumentsComplete(value: DocumentsDetails = {} as DocumentsDetails, isRTM = false) {
// 	const requiredFields = isRTM
// 		? ["landKhathaAuthority", "landKhatha"]
// 		: ["constructionKhathaAuthority", "constructionKhatha", "landKhathaAuthority", "landKhatha"];

// 	return requiredFields.every(fieldName => {
// 		const fieldValue = value[fieldName as keyof DocumentsDetails];
// 		return fieldValue !== undefined && fieldValue !== null && fieldValue !== "";
// 	});
// }

interface FormSectionProps {
	title: string;
	description?: string;
	children: React.ReactNode;
	onClear?: () => void;
}

/**
 * Component for rendering a form section with title and optional clear button
 */
const FormSection = memo(({ title, description, children, onClear: _onClear }: FormSectionProps) => (
	<div className="space-y-3 w-full md:space-y-4">
		<div className="flex items-center justify-between p-3 bg-muted rounded-lg border border-border md:p-4">
			<div>
				<h4 className="text-sm font-semibold text-card-foreground md:text-base flex items-center gap-2">
					<FileText className="w-4 h-4" />
					{title}
				</h4>
				{description && <p className="text-xs text-muted-foreground mt-1">{description}</p>}
			</div>
			{/* {onClear && (
				<Button
					type="button"
					variant="ghost"
					size="sm"
					onClick={onClear}
					aria-label={`Clear ${title}`}
					className="ml-2"
				>
					<X className="w-4 h-4 text-destructive" aria-hidden="true" />
				</Button>
			)} */}
		</div>
		<div className="space-y-3 md:space-y-4">{children}</div>
	</div>
));

FormSection.displayName = "FormSection";

/**
 * Documents Details Form Section Component
 */
const DocumentsDetailsSection = memo(
	({
		name,
		isRTM = false,
		title = "Khatha Information",
		description = "Provide khatha details for your property",
	}: {
		name: string;
		isRTM?: boolean;
		title?: string;
		description?: string;
	}) => {
		const { control, watch, setValue, clearErrors } = useFormContext();

		// Memoize authority options to prevent recalculation on every render
		const constructionAuthorityOptions = useMemo(() => getAuthorityOptions("isConstructionDoc"), []);
		const landAuthorityOptions = useMemo(() => getAuthorityOptions("isLandDoc"), []);

		// Watch authority selections to update khatha options
		const constructionKhathaAuthority = watch(`${name}.constructionKhathaAuthority`);
		const landKhathaAuthority = watch(`${name}.landKhathaAuthority`);

		// Memoize khatha options based on selected authorities
		const constructionKhathaOptions = useMemo(
			() => (constructionKhathaAuthority ? getKhathaOptions("constructionKhatha", constructionKhathaAuthority) : []),
			[constructionKhathaAuthority],
		);

		const landKhathaOptions = useMemo(
			() => (landKhathaAuthority ? getKhathaOptions("landKhatha", landKhathaAuthority) : []),
			[landKhathaAuthority],
		);

		// Clear khatha selection when authority changes
		useEffect(() => {
			const currentValue = watch(`${name}.constructionKhatha`);
			if (constructionKhathaAuthority && currentValue) {
				// Only clear if the current khatha option is not available for the selected authority
				const availableOptions = getKhathaOptions("constructionKhatha", constructionKhathaAuthority);
				const isCurrentValueValid = availableOptions.some(option => option.value === currentValue);

				if (!isCurrentValueValid) {
					setValue(`${name}.constructionKhatha`, "", { shouldDirty: true });
					clearErrors(`${name}.constructionKhatha`);
				}
			}
		}, [constructionKhathaAuthority, setValue, clearErrors, name, watch]);

		useEffect(() => {
			const currentValue = watch(`${name}.landKhatha`);
			if (landKhathaAuthority && currentValue) {
				// Only clear if the current khatha option is not available for the selected authority
				const availableOptions = getKhathaOptions("landKhatha", landKhathaAuthority);
				const isCurrentValueValid = availableOptions.some(option => option.value === currentValue);

				if (!isCurrentValueValid) {
					setValue(`${name}.landKhatha`, "", { shouldDirty: true });
					clearErrors(`${name}.landKhatha`);
				}
			}
		}, [landKhathaAuthority, setValue, clearErrors, name, watch]);

		const createFieldRules = (fieldName: string) => {
			// Make khatha fields mandatory
			const requiredFields: string[] = isRTM
				? ["landKhathaAuthority", "landKhatha"]
				: ["constructionKhathaAuthority", "constructionKhatha", "landKhathaAuthority", "landKhatha"];

			return requiredFields.includes(fieldName)
				? {
						required: {
							value: true,
							message: `${fieldName.replace(/([A-Z])/g, " $1").replace(/^./, str => str.toUpperCase())} is required`,
						},
					}
				: undefined;
		};

		const hClear = () => {
			// Always clear these
			setValue(`${name}.approvalDocs`, [], { shouldDirty: true });
			clearErrors(`${name}.approvalDocs`);

			// Only clear construction fields if not RTM
			if (!isRTM) {
				setValue(`${name}.constructionKhathaAuthority`, "", { shouldDirty: true });
				setValue(`${name}.constructionKhatha`, "", { shouldDirty: true });
				clearErrors(`${name}.constructionKhathaAuthority`);
				clearErrors(`${name}.constructionKhatha`);
			}

			// Always clear land fields
			setValue(`${name}.landKhathaAuthority`, "", { shouldDirty: true });
			setValue(`${name}.landKhatha`, "", { shouldDirty: true });
			clearErrors(`${name}.landKhathaAuthority`);
			clearErrors(`${name}.landKhatha`);
		};

		return (
			<FormSection title={title} description={description} onClear={hClear}>
				<div className="space-y-4 lg:space-y-5">
					{/* Documents Section */}
					<div className="space-y-3">
						<div className="text-sm font-medium text-card-foreground flex items-center gap-2 px-3">
							<div className="w-1 h-4 bg-primary rounded-full"></div>
							Documents
						</div>
						<div className="p-4 bg-muted/30 rounded-lg border border-dashed border-border">
							<Input
								label="Approval Documents"
								name={`${name}.approvalDocs`}
								type="checkbox"
								options={nonKhataDocumentOptions.map(doc => ({
									label: doc.name || doc.code,
									value: doc.code,
								}))}
								control={control}
								itemsClassName="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3"
							/>
						</div>
					</div>

					{/* Construction Khatha Section - Hide for RTM */}
					{!isRTM && (
						<div className="space-y-3">
							<div className="text-sm font-medium text-card-foreground flex items-center gap-2 px-3">
								<div className="w-1 h-4 bg-orange-500 rounded-full"></div>
								Construction Khatha Information
							</div>
							<div className="p-4 bg-orange-50/50 dark:bg-orange-950/20 rounded-lg border border-dashed border-orange-200 dark:border-orange-800 space-y-4">
								{/* Construction Khatha Authority */}
								<Input
									label="Construction Khatha Authority"
									name={`${name}.constructionKhathaAuthority`}
									type="radio"
									options={constructionAuthorityOptions}
									control={control}
									itemsClassName="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 gap-2"
									rules={createFieldRules("constructionKhathaAuthority")}
								/>

								{/* Construction Khatha Type - Only show if authority is selected */}
								{constructionKhathaAuthority && (
									<Input
										label="Construction Khatha Type"
										name={`${name}.constructionKhatha`}
										type="radio"
										itemsClassName="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 gap-2"
										options={constructionKhathaOptions}
										control={control}
										rules={createFieldRules("constructionKhatha")}
									/>
								)}
							</div>
						</div>
					)}

					{/* Land Khatha Section */}
					<div className="space-y-3">
						<div className="text-sm font-medium text-card-foreground flex items-center gap-2 px-3">
							<div className="w-1 h-4 bg-green-500 rounded-full"></div>
							Land Khatha Information
						</div>
						{/* Information Note */}
						<div className="px-3">
							<p className="text-xs leading-relaxed">
								<strong>Note:</strong> Please ensure all khatha information is accurate. If you&apos;re unsure about the
								khatha type, select &quot;Unsure/Others&quot; and provide additional details later.
							</p>
						</div>
						<div className="p-4 bg-green-50/50 dark:bg-green-950/20 rounded-lg border border-dashed border-green-200 dark:border-green-800 space-y-4">
							{/* Land Khatha Authority */}
							<Input
								label="Land Khatha Authority"
								name={`${name}.landKhathaAuthority`}
								type="radio"
								itemsClassName="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 gap-2"
								options={landAuthorityOptions}
								control={control}
								rules={createFieldRules("landKhathaAuthority")}
							/>

							{/* Land Khatha Type - Only show if authority is selected */}
							{landKhathaAuthority && (
								<Input
									label="Land Khatha Type"
									name={`${name}.landKhatha`}
									type="radio"
									itemsClassName="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 gap-2"
									options={landKhathaOptions}
									control={control}
									rules={createFieldRules("landKhatha")}
								/>
							)}
						</div>
					</div>
				</div>
			</FormSection>
		);
	},
);

DocumentsDetailsSection.displayName = "DocumentsDetailsSection";

export { DocumentsDetailsSection, nonKhataDocumentOptions };
